// includes/js-src/janus-engine/pl/read-context.ts — the bridged layer readers the PL pricer prices THROUGH (Janus C2).
// Same construction as the C1 entry's readerDeps (compile/possessor-compile.ts:208) over the frozen corpus:
// createStatusLayerReadContext → layer_value / layer_entries / layer_has twins, bound to a definition resolver.
// Plan: plans/janus-c2-pl-in-browser-plan-2026-09-16.md §6.1–§6.3, §14 amendment 5.
import type { ItemFieldRow } from '../compile/item-field-reader';
import { readItemField } from '../compile/item-field-reader';
import { readInventoryField } from '../compile/inventory-raw-readers';
import { createStatusLayerReadContext } from '../compile/status-row-construction';
import c1Contract from '../compile/c1-contract.json';
import { checkUuid } from './contract';
import type { AbilityEntryDependencies } from './layer-cells';
import { PlRefusal } from './refusal';

export type Row = Record<string, unknown>;
export const isObj = (v: unknown): v is Row => typeof v === 'object' && v !== null && !Array.isArray(v);
export const upper = (v: unknown): string => String(v ?? '').toUpperCase();

/** How the entry sees a definition: present, or refused with a reason the store recorded (never inferred from null). */
export type DefinitionLookup = { def: Row } | { missing: true } | { forbidden: true };

export interface PlReadContextInputs {
    /** encounterArr.catalog — FULL definitions the page already holds (a def with item_layer_types). */
    catalog?: Row | null;
    /** The definition store (or the fixture) — an explicit result per id. */
    lookup: (id: string) => DefinitionLookup;
    /** The instances the carrier holds post-mutation (thin rows); the inventory pass and the readers' instance bridge use them. */
    instances?: readonly Row[];
}

export interface PlReadContext {
    layerValue: (row: ItemFieldRow | null, name: string) => unknown;
    layerEntries: (row: ItemFieldRow | null, name: string) => ItemFieldRow;
    hasLayer: (row: ItemFieldRow | null, name: string) => boolean;
    /** ft_inventory_thin_scalar_read twin (local value, else the template's). */
    thinScalar: (row: ItemFieldRow, name: string) => unknown;
    /** item_field() bound to the same resolvers. */
    itemField: (row: ItemFieldRow | null, key: string) => unknown;
    /** A FULL definition by id, or null; records the refusal reason for the closure report. */
    definition: (id: unknown) => Row | null;
    /** The STORE's copy only (never the page catalog's interned shell) — for the stored bare PL a cross-reference reads. */
    storedDefinition: (id: unknown) => Row | null;
    /** Every id asked for and not held, with the store's reason. */
    readonly refused: Map<string, 'missing' | 'forbidden'>;
    /** C2d: every definition id this context RESOLVED (catalog or store) — what a price actually read; the claim's closure must hold them all. */
    readonly reads: Set<string>;
    /** Non-item pricing inputs (currently monster-template names), bound to their ft:def_rev revision. */
    readonly auxRevs: Map<string, number>;
    /** Resolve the display name used by the server's summon-template multiplier and record its auxiliary revision. */
    monsterTemplateName: (row: ItemFieldRow | null, id: unknown) => string | null;
    readonly entryDeps: AbilityEntryDependencies;
    readonly catalogRows: Record<string, { default_provider?: number; aggregation_strategy?: string }>;
    readonly abilityTypes: readonly string[];
}

function fatInstance(thin: Row, def: Row): Row {
    const fat: Row = { ...thin };
    fat.items = { ...(isObj(def.items) ? def.items : {}), ...(isObj(thin.items) ? thin.items : {}) };
    fat.item_layer_types = isObj(def.item_layer_types) ? def.item_layer_types : {};
    if (isObj(def.catalog)) fat.catalog = def.catalog;
    return fat;
}

export function createPlReadContext(inputs: PlReadContextInputs): PlReadContext {
    const contract = c1Contract as unknown as { catalogRows: PlReadContext['catalogRows']; abilityTypes: string[] };
    const refused = new Map<string, 'missing' | 'forbidden'>();
    const reads = new Set<string>();
    const auxRevs = new Map<string, number>();
    const held = new Map<string, Row>();
    const definition = (id: unknown): Row | null => {
        if (!checkUuid(id)) return null;
        const key = id.toUpperCase();
        const cached = held.get(key);
        if (cached) { reads.add(key); return cached; }
        // A usable definition has an items bucket with an id, and — when present — an item_layer_types MAP (PHP serialises an
        // empty map as []; a definition with zero layers omits the key). `{def:{}}` or a string-valued item_layer_types is an
        // incomplete payload, never a definition (Codex review). A CATALOG entry counts only when it carries item_layer_types
        // (the interned shells do not — the C1 rule).
        const layersOk = (d: Row): boolean => d.item_layer_types === undefined || isObj(d.item_layer_types) || Array.isArray(d.item_layer_types);
        const usable = (d: unknown): d is Row => isObj(d) && isObj(d.items) && typeof d.items.item_id === 'string' && layersOk(d);
        const fromCatalog = isObj(inputs.catalog) ? (inputs.catalog[key] ?? inputs.catalog[id]) : undefined;
        if (usable(fromCatalog) && fromCatalog.item_layer_types !== undefined) { held.set(key, fromCatalog); reads.add(key); return fromCatalog; }
        const r = inputs.lookup(key);
        if ('def' in r && usable(r.def)) { held.set(key, r.def); reads.add(key); return r.def; }
        refused.set(key, 'forbidden' in r ? 'forbidden' : 'missing');
        return null;
    };
    const storedDefinition = (id: unknown): Row | null => {
        if (!checkUuid(id)) return null;
        const r = inputs.lookup(id.toUpperCase());
        if ('def' in r && isObj(r.def)) { reads.add(id.toUpperCase()); return r.def; }
        return null;
    };
    const identity = (id: string): Row | null => {
        const def = definition(id);
        if (!def) return null;
        const items = isObj(def.items) ? def.items : {};
        return { item_id: items.item_id ?? id, item_name: items.item_name ?? null, item_type: items.item_type ?? null, item_private: items.item_private ?? null };
    };
    const instances = inputs.instances ?? [];
    const monsterTemplateName = (row: ItemFieldRow | null, id: unknown): string | null => {
        if (!checkUuid(id)) return null;
        const key = id.toUpperCase();
        const rowCatalog = isObj(row) && isObj(row.catalog) ? row.catalog : null;
        const shell = rowCatalog?.[key] ?? rowCatalog?.[id] ?? (isObj(inputs.catalog) ? (inputs.catalog[key] ?? inputs.catalog[id]) : undefined);
        if (!isObj(shell) || shell.item_type !== 'monster_template' || typeof shell.item_name !== 'string'
            || !Number.isSafeInteger(shell._pl_rev) || (shell._pl_rev as number) < 0) return null;
        auxRevs.set(key, shell._pl_rev as number);
        return shell.item_name;
    };
    const deps = {
        catalogRows: contract.catalogRows, abilityTypes: contract.abilityTypes,
        aggregationStrategy: (name: string) => contract.catalogRows[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: (id: string) => definition(id) as ItemFieldRow | null,
        resolveInventoryInstance: (id: string): ItemFieldRow | null => {
            const thin = instances.find(r => upper(readInventoryField(r as never, 'inventory_id')) === upper(id));
            if (!thin) return null;
            const def = definition(readInventoryField(thin as never, 'inventory_item_id'));
            return def ? fatInstance(thin, def) as ItemFieldRow : null;
        },
        resolveItemName: (id: string) => identity(id)?.item_name ?? null,
        resolveInventoryIdentity: (id: string) => identity(id) as ItemFieldRow | null,
        queryTemplateDefinition: (id: string) => definition(id) as ItemFieldRow | null,
    };
    const ctx = createStatusLayerReadContext(deps);
    return {
        layerValue: ctx.layerValue,
        layerEntries: (row, name) => row === null ? [] : ctx.layerEntries(row, name),
        hasLayer: (row, name) => row === null ? false : ctx.hasLayer(row, name),
        thinScalar: (row, name) => {
            // ft_inventory_thin_scalar_read: the local (bridged) read first; a thinned instance falls back to its template.
            const local = ctx.layerValue(row, name);
            if (local !== null && local !== undefined) return local;
            const tplId = readInventoryField(row as never, 'inventory_item_id');
            if (!checkUuid(tplId)) return null;
            const tpl = definition(tplId);
            // A template the browser cannot see is a REFUSAL, not an absent value (Codex review: the inventory pass would
            // otherwise price the instance at 0 gp in silence).
            if (!tpl) throw new PlRefusal((refused.get(tplId.toUpperCase()) === 'forbidden' ? 'pl-def-forbidden:' : 'pl-def-missing:') + tplId.toUpperCase());
            return ctx.layerValue(tpl as ItemFieldRow, name);
        },
        itemField: (row, key) => readItemField(row, key, deps),
        definition,
        storedDefinition,
        refused,
        reads,
        auxRevs,
        monsterTemplateName,
        entryDeps: { resolveCarrierRef: deps.resolveCarrierRef, resolveItemName: deps.resolveItemName, resolveInventoryIdentity: deps.resolveInventoryIdentity },
        catalogRows: contract.catalogRows,
        abilityTypes: contract.abilityTypes,
    };
}
